import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeWithRetry } from '@/lib/api-client';
import { useAuth } from './AuthContext';

export interface Purchase {
  purchase_type: 'order' | 'subscription';
  product_id: string;
  product_name: string;
  status: string;
  created_at: string;
  subscription_end?: string;
}

interface PaywallContextType {
  purchases: Purchase[];
  isLoading: boolean;
  hasPurchased: (productId: string) => boolean;
  hasActiveSubscription: (deviceType: string) => boolean;
  refreshPurchases: () => Promise<void>;
  createPayment: (productId: string) => Promise<string>;
  createGuestPayment: (productId: string) => Promise<string>;
  createSubscription: (deviceType: string) => Promise<string>;
}

const PaywallContext = createContext<PaywallContextType | undefined>(undefined);

export const usePaywall = (): PaywallContextType => {
  const context = useContext(PaywallContext);
  if (!context) {
    throw new Error('usePaywall must be used within a PaywallProvider');
  }
  return context;
};

export const PaywallProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  const refreshPurchases = async () => {
    if (!user) {
      setPurchases([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      
      // Fetch user purchases using the secure function
      const { data, error } = await supabase.rpc('get_user_purchases');

      if (error) {
        console.error('Error fetching purchases:', error);
        return;
      }

      setPurchases((data || []) as Purchase[]);
    } catch (error) {
      console.error('Error refreshing purchases:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshPurchases();
  }, [user]);

  const hasPurchased = (productId: string): boolean => {
    return purchases.some(
      purchase => purchase.product_id === productId && 
      (purchase.status === 'paid' || purchase.status === 'active')
    );
  };

  const hasActiveSubscription = (deviceType: string): boolean => {
    const subscription = purchases.find(
      purchase => purchase.purchase_type === 'subscription' && 
      purchase.product_id === deviceType && 
      purchase.status === 'active'
    );

    if (!subscription?.subscription_end) return false;

    const endDate = new Date(subscription.subscription_end);
    return endDate > new Date();
  };

  const createPayment = async (productId: string): Promise<string> => {
    if (!user) throw new Error('Authentication required for payments');

    try {
      const data = await invokeWithRetry(
        'create-lemonsqueezy-payment',
        { product_id: productId },
        { maxRetries: 2, baseDelayMs: 1500 }
      );

      if (!data?.url) {
        throw new Error('No checkout URL returned from payment provider');
      }

      return data.url;
    } catch (error: any) {
      console.error('Create payment error:', error);
      throw new Error(
        error?.message ?? 'An unexpected error occurred while creating the payment'
      );
    }
  };

  const createGuestPayment = async (productId: string): Promise<string> => {
    throw new Error('Guest payments are no longer supported for security reasons. Please sign in to make a purchase.');
  };

  const createSubscription = async (deviceType: string): Promise<string> => {
    if (!user) throw new Error('Authentication required for subscriptions');

    try {
      const data = await invokeWithRetry(
        'create-lemonsqueezy-subscription',
        { device_type: deviceType },
        { maxRetries: 2, baseDelayMs: 1500 }
      );

      if (!data?.url) {
        throw new Error('No checkout URL returned from payment provider');
      }

      return data.url;
    } catch (error: any) {
      console.error('Create subscription error:', error);
      throw new Error(
        error?.message ?? 'An unexpected error occurred while creating the subscription'
      );
    }
  };

  return (
    <PaywallContext.Provider
      value={{
        purchases,
        isLoading,
        hasPurchased,
        hasActiveSubscription,
        refreshPurchases,
        createPayment,
        createGuestPayment,
        createSubscription,
      }}
    >
      {children}
    </PaywallContext.Provider>
  );
};